import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'

import { useAuthContext } from '@/context-state/AuthContext.jsx'

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { TypographySmall, TypographySmallMuted } from '@/components/ui/typography.jsx'
import { Icon } from '@iconify/react'

import Logo from '@/assets/logos/spring.png'

// Shown right after login when the user belongs to more than one account. Picking
// an account navigates into it (/account/:accountId/space); everything downstream
// is then scoped to that chosen account. To switch accounts the user logs out and
// back in.
export default function AccountSelectorPage() {

    const { accounts, spaces, logout } = useAuthContext()
    const navigate = useNavigate()

    const handleSelectAccount = (accountId) => {
        navigate(`/account/${accountId}/space`)
    }

    // Count the spaces that belong to a given account (spaces is the flat list of
    // every space across all of the user's accounts).
    const getSpaceCount = (accountId) => {
        return spaces.filter(space => (space.account?._id || space.account) === accountId).length
    }

    return (
        <div className="min-h-screen w-full flex items-center justify-center p-4 bg-main-surface bg-center bg-cover bg-no-repeat">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', bounce: 0.35 }}
                className="w-full max-w-md mx-auto"
            >
                <Card className="w-full shadow-lg backdrop-blur-md bg-background/70">
                    <CardHeader>
                        <CardTitle className="text-center">
                            <img src={Logo} className="m-auto w-32" alt="upspring-logo" />
                        </CardTitle>
                        <CardDescription className="text-center">
                            Choose an account to continue
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2">
                        {
                            accounts.map((account, index) => {
                                const spaceCount = getSpaceCount(account._id)
                                return (
                                    <motion.button
                                        key={account._id}
                                        type="button"
                                        onClick={() => handleSelectAccount(account._id)}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="group flex items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent hover:border-primary/40"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                                                <Icon icon="mdi:office-building-outline" className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0">
                                                <TypographySmall className="block truncate">
                                                    {account.name || 'Untitled account'}
                                                </TypographySmall>
                                                <TypographySmallMuted className="block truncate">
                                                    {spaceCount} {spaceCount === 1 ? 'space' : 'spaces'}
                                                </TypographySmallMuted>
                                            </div>
                                        </div>
                                        <Icon
                                            icon="lucide:chevron-right"
                                            className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                                        />
                                    </motion.button>
                                )
                            })
                        }
                    </CardContent>
                    <CardFooter className="justify-center">
                        <button
                            type="button"
                            onClick={logout}
                            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                            <Icon icon="lucide:log-out" className="h-4 w-4" />
                            Log out
                        </button>
                    </CardFooter>
                </Card>
            </motion.div>
        </div>
    )
}
